"use client";

import { type RefObject, useEffect, useRef, useState } from "react";
import { useFeatureFlag } from "@/lib/feature-flags";
import { decodeAudioBackgroundImage, drawCoverImage } from "./audioBackgroundImage";
import {
  type AudioWaveformConfig,
  drawWaveformBars,
  spectrumToBars,
  waveformStyleFromConfig,
} from "./audioWaveform";
import { type CaptionPreviewSpec, drawCaptionFrame } from "./captions";
import { containRect } from "./videoRect";

interface AudioAnalyserState {
  element: HTMLMediaElement;
  context: AudioContext;
  analyser: AnalyserNode;
  outputGain: GainNode;
  data: Uint8Array<ArrayBuffer>;
}

const audioAnalyserCache = new WeakMap<HTMLMediaElement, AudioAnalyserState>();

function createAudioContext(): AudioContext | null {
  const AudioContextCtor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  return AudioContextCtor ? new AudioContextCtor() : null;
}

/**
 * Live caption preview: a transparent canvas laid over the source `<video>` that
 * draws the chosen captions/style with the **same renderer as the burn**, so the
 * preview matches the export by construction.
 *
 * Two things make it match rather than approximate:
 * - It positions itself over the video's *content rect* (the `object-contain`
 *   picture inside the letterboxed element), not the element box — recomputed on
 *   resize and metadata.
 * - It backs the canvas at the **intrinsic media resolution** and draws at that
 *   size, then lets CSS scale it down to the content rect. This is the same basis
 *   the burn/Lambda export lays out at, so wrapping and font metrics match the
 *   export exactly — `ctx.measureText` isn't linear across font sizes, so drawing
 *   the preview at the small display size would wrap lines differently than the
 *   full-resolution export. (Backing at intrinsic res is also crisper than the
 *   old `devicePixelRatio`-scaled display canvas for typical 1080p+ media.)
 *
 * Timing comes from `requestVideoFrameCallback` while playing (which only fires
 * on presented frames), with explicit redraws on seek/pause/resize and on every
 * spec change — so a paused frame updates the instant you switch presets. The
 * canvas is `pointer-events:none` so the native controls stay usable. It does
 * not follow the video into native fullscreen (a deliberate scope decision).
 */
export function CaptionOverlay({
  videoRef,
  spec,
  sourceSize,
  audioWaveformUrl,
  audioWaveform,
  audioBackgroundImageDataUrl,
  audioWaveformOutputMuted = false,
}: {
  videoRef: RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  spec: CaptionPreviewSpec | null;
  /**
   * Intrinsic frame size to use when the element has no video track (audio-only
   * media). Pass {@link AUDIO_RENDER_SIZE}; omit for video, where the real
   * `videoWidth/Height` are used.
   */
  sourceSize?: { width: number; height: number };
  /** Audio source to visualize for audio-only media. Ignored unless the flag is on. */
  audioWaveformUrl?: string | null;
  /** Per-document waveform visual settings. Null/disabled means a plain audio-only frame. */
  audioWaveform?: AudioWaveformConfig | null;
  /** Compressed base64 data URL for the audio-only background image. */
  audioBackgroundImageDataUrl?: string | null;
  /** Mute the analyser's audible passthrough when another audio graph owns playback. */
  audioWaveformOutputMuted?: boolean;
}) {
  const audioVisualsEnabled = useFeatureFlag("audioVisuals");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const specRef = useRef(spec);
  specRef.current = spec;
  const audioWaveformRef = useRef(audioWaveform);
  audioWaveformRef.current = audioWaveform;
  const audioWaveformEnabled = Boolean(
    audioVisualsEnabled && audioWaveformUrl && audioWaveform?.enabled
  );
  const audioWaveformEnabledRef = useRef(audioWaveformEnabled);
  audioWaveformEnabledRef.current = audioWaveformEnabled;
  const audioBackgroundEnabled = Boolean(audioVisualsEnabled && audioBackgroundImageDataUrl);
  const audioBackgroundEnabledRef = useRef(audioBackgroundEnabled);
  audioBackgroundEnabledRef.current = audioBackgroundEnabled;
  const audioAnalyserRef = useRef<AudioAnalyserState | null>(null);
  const audioWaveformOutputMutedRef = useRef(audioWaveformOutputMuted);
  audioWaveformOutputMutedRef.current = audioWaveformOutputMuted;
  const audioBackgroundRef = useRef<HTMLImageElement | ImageBitmap | null>(null);
  const [audioBackground, setAudioBackground] = useState<HTMLImageElement | ImageBitmap | null>(
    null
  );
  audioBackgroundRef.current = audioBackground;
  // Hold the live draw fn so the spec-change effect can trigger a redraw
  // without re-subscribing the rVFC loop and resize observer.
  const drawRef = useRef<() => void>(() => {});
  const sw = sourceSize?.width;
  const sh = sourceSize?.height;

  useEffect(() => {
    if (!audioBackgroundEnabled || !audioBackgroundImageDataUrl) {
      setAudioBackground((current) => {
        if (current && "close" in current) current.close();
        return null;
      });
      return;
    }

    let cancelled = false;
    setAudioBackground((current) => {
      if (current && "close" in current) current.close();
      return null;
    });
    void decodeAudioBackgroundImage(audioBackgroundImageDataUrl)
      .then((next) => {
        if (cancelled) {
          if ("close" in next) next.close();
          return;
        }
        setAudioBackground(next);
      })
      .catch(() => {
        if (!cancelled) setAudioBackground(null);
      });

    return () => {
      cancelled = true;
    };
  }, [audioBackgroundEnabled, audioBackgroundImageDataUrl]);

  // Subscribes once to the video element (read via the stable videoRef); the live
  // spec is read through specRef, and spec changes trigger a redraw via the effect below.
  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !(video instanceof HTMLVideoElement) || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const analyserForVideo = (): AudioAnalyserState | null => {
      const existing = audioAnalyserRef.current;
      if (existing?.element === video) return existing;
      const cached = audioAnalyserCache.get(video);
      if (cached) {
        audioAnalyserRef.current = cached;
        return cached;
      }
      const context = createAudioContext();
      if (!context) return null;
      let source: MediaElementAudioSourceNode;
      try {
        source = context.createMediaElementSource(video);
      } catch {
        void context.close().catch(() => {});
        return null;
      }
      const analyser = context.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.72;
      const outputGain = context.createGain();
      source.connect(analyser);
      analyser.connect(outputGain).connect(context.destination);
      const next = {
        element: video,
        context,
        analyser,
        outputGain,
        data: new Uint8Array(analyser.frequencyBinCount),
      };
      audioAnalyserCache.set(video, next);
      audioAnalyserRef.current = next;
      return next;
    };

    /**
     * Position the canvas over the letterboxed picture rect (CSS px) and back its
     * pixel buffer at the intrinsic media resolution; returns that intrinsic size,
     * which is what we draw captions at (matching the export's layout basis). For
     * audio-only media (no video track) `videoWidth/Height` are 0, so fall back to
     * `sourceSize` — the same frame the export uses.
     */
    const fitCanvas = (): { width: number; height: number } | null => {
      const vw = video.videoWidth || sw || 0;
      const vh = video.videoHeight || sh || 0;
      const { clientWidth: cw, clientHeight: ch } = video;
      if (!vw || !vh || !cw || !ch) return null;
      const r = containRect(vw, vh, cw, ch);
      canvas.style.left = `${r.left}px`;
      canvas.style.top = `${r.top}px`;
      canvas.style.width = `${r.width}px`;
      canvas.style.height = `${r.height}px`;
      if (canvas.width !== vw || canvas.height !== vh) {
        canvas.width = vw;
        canvas.height = vh;
      }
      return { width: vw, height: vh };
    };

    const draw = () => {
      const size = fitCanvas();
      if (!size) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size.width, size.height);
      const current = specRef.current;
      const background = audioBackgroundRef.current;
      if (audioBackgroundEnabledRef.current && background) {
        drawCoverImage(ctx, background, size.width, size.height);
      }
      const waveform = audioWaveformRef.current;
      if (audioWaveformEnabledRef.current && current && waveform?.enabled) {
        const analyser = analyserForVideo();
        if (analyser) {
          if (analyser.context.state === "suspended" && !video.paused) {
            void analyser.context.resume().catch(() => {});
          }
          analyser.outputGain.gain.value = audioWaveformOutputMutedRef.current ? 0 : 1;
          const style = waveformStyleFromConfig(current.style, waveform);
          analyser.analyser.getByteFrequencyData(analyser.data);
          const bins = spectrumToBars(analyser.data, style.barCount, 255);
          drawWaveformBars(ctx, bins, style, size.width, size.height);
        }
      }
      if (current && current.cues.length > 0) {
        drawCaptionFrame(
          ctx,
          current.cues,
          current.style,
          video.currentTime,
          size.width,
          size.height
        );
      }
    };
    drawRef.current = draw;

    // Audio-only media (sourceSize set) plays through a video element with no
    // presented frames, so `requestVideoFrameCallback` never fires — fall back to
    // rAF so the caption keeps redrawing as the playhead advances.
    const hasVfc = sw == null && "requestVideoFrameCallback" in video;
    let rafId = 0;
    let vfcId = 0;
    const loop = () => {
      draw();
      // rVFC re-arms only while frames are presented (i.e. during playback);
      // static redraws are covered by the event listeners below.
      if (audioWaveformEnabledRef.current) rafId = requestAnimationFrame(loop);
      else if (hasVfc) vfcId = video.requestVideoFrameCallback(loop);
      else rafId = requestAnimationFrame(loop);
    };
    loop();

    const observer = new ResizeObserver(draw);
    observer.observe(video);
    video.addEventListener("seeked", draw);
    video.addEventListener("pause", draw);
    video.addEventListener("loadedmetadata", draw);
    // Repaint when a webfont finishes loading (a paused frame drawn with the
    // fallback font is otherwise stale until the next seek/play).
    document.fonts?.addEventListener("loadingdone", draw);

    return () => {
      observer.disconnect();
      video.removeEventListener("seeked", draw);
      video.removeEventListener("pause", draw);
      video.removeEventListener("loadedmetadata", draw);
      document.fonts?.removeEventListener("loadingdone", draw);
      if (hasVfc && vfcId) video.cancelVideoFrameCallback(vfcId);
      if (rafId) cancelAnimationFrame(rafId);
      audioAnalyserRef.current = null;
      drawRef.current = () => {};
    };
  }, [videoRef, sw, sh]);

  // Redraw immediately when the spec changes — covers a paused video, where the
  // rVFC loop is idle.
  useEffect(() => {
    drawRef.current();
  }, [audioBackground, audioWaveform, spec]);

  return <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none absolute" />;
}
